import { CUSTOM_MESSAGE_TYPE } from 'components/Chat/constants/messageTypes'
import React, { useEffect, useRef, useState } from 'react'
import get from 'lodash/get'
import {
  MessageDeleted as DefaultMessageDeleted,
  areMessageUIPropsEqual,
  useComponentContext,
  MessageContextValue,
  useMessageContext,
  MessageUIComponentProps,
  useChatContext,
  Attachment,
  renderText,
} from 'stream-chat-react'
import DOMPurify from 'dompurify'

import type { DefaultStreamChatGenerics } from '../../types'
import ExclamationMarkIcon from 'components/Common/SvgIcons/ExclamationMarkIcon'
import { useUserInfo } from 'hooks/useUserInfo'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import { getInitials, getInitialsFromName } from 'components/Chat/helper'

// Import SVG icons
import { ReactComponent as ReplyIcon } from 'assets/svgs/arrow-uturn-left-icon.svg'
import { ReactComponent as ForwardIcon } from 'assets/svgs/user-icon.svg'
import { ReactComponent as StarIconSvg } from 'assets/svgs/star-icon.svg'
import { ReactComponent as EditIcon } from 'assets/svgs/pencil-square.svg'
import { ReactComponent as DeleteIcon } from 'assets/svgs/trash.svg'
import { ReactComponent as ThreeDotsIconSvg } from 'assets/svgs/three-dots-icon.svg'
import { ReactComponent as PenIconSvg } from 'assets/svgs/pen-icon.svg'
import { ReactComponent as CopyIconSvg } from 'assets/svgs/copy-icon.svg'

import { DeleteMessageModal } from './DeleteMessageModal'
import { ForwardMessageModal } from './ForwardMessageModal'
import { EditMessageForm } from './EditMessageForm'
import { MessageOptions } from './MessageOptions'
import { SimpleReactionsList } from '../Reactions/SimpleReactionsList'
import { ReactionSelector } from '../Reactions/ReactionSelector'
import { VoiceMessage } from '../VoiceMessage'
import ReactDOM from 'react-dom'

/* =========================
   Helpers (outside component)
========================= */
const isScrollable = (el: Element) => {
  const { overflow, overflowY, overflowX } = getComputedStyle(el)
  return /(auto|scroll|overlay)/.test(`${overflow}${overflowY}${overflowX}`)
}

const getScrollParents = (node: HTMLElement | null) => {
  const parents: HTMLElement[] = []
  if (!node) return parents
  let p = node.parentElement
  while (p) {
    if (isScrollable(p)) parents.push(p)
    p = p.parentElement
  }
  return parents
}

// Body scroll lock (no layout shift)
const lockBodyScroll = () => {
  const scrollY = window.scrollY || window.pageYOffset
  const scrollbarW = window.innerWidth - document.documentElement.clientWidth
  document.body.dataset.scrollY = String(scrollY)
  if (scrollbarW > 0) document.body.style.paddingRight = `${scrollbarW}px`
  document.body.style.position = 'fixed'
  document.body.style.top = `-${scrollY}px`
  document.body.style.left = '0'
  document.body.style.right = '0'
  document.body.style.width = '100%'
  document.body.style.overflow = 'hidden'
}

const unlockBodyScroll = () => {
  const y = Number(document.body.dataset.scrollY || 0)
  document.body.style.position = ''
  document.body.style.top = ''
  document.body.style.left = ''
  document.body.style.right = ''
  document.body.style.width = ''
  document.body.style.overflow = ''
  document.body.style.paddingRight = ''
  delete document.body.dataset.scrollY
  window.scrollTo(0, y)
}

// Element scroll lock (chat scroller, etc.)
type LockedEl = {
  el: HTMLElement
  prevOverflow: string
  prevOverscroll: string
  prevScrollTop: number
}
const lockElementScroll = (el: HTMLElement): LockedEl => {
  const prevOverflow = el.style.overflow
  const prevOverscroll = (el.style as any).overscrollBehavior || ''
  const prevScrollTop = el.scrollTop
  el.style.overflow = 'hidden'
  ;(el.style as any).overscrollBehavior = 'contain'
  return { el, prevOverflow, prevOverscroll, prevScrollTop }
}
const unlockElementScroll = (lock: LockedEl) => {
  lock.el.style.overflow = lock.prevOverflow
  ;(lock.el.style as any).overscrollBehavior = lock.prevOverscroll
  lock.el.scrollTop = lock.prevScrollTop
}

/* ========================= */

// Inline Portal component if not available elsewhere
const Portal = ({ children }) => {
  if (typeof window === 'undefined') return null
  return ReactDOM.createPortal(children, document.body)
}

// Three Dots Icon Component
const ThreeDotsIcon = () => <ThreeDotsIconSvg className="w-4 h-4 text-app-grey-60" />

const PenIcon = () => <PenIconSvg className="w-4 h-4 text-app-grey-60" />

const CopyIcon = () => <CopyIconSvg className="w-4 h-4 text-app-grey-60" />

// Star Icon Component wrapper to handle filled state
const StarIcon = ({ filled = false }) => (
  <StarIconSvg
    className={`w-5 h-5 ${filled ? 'text-app-grey-65' : 'text-app-grey-65'}`}
    style={{
      stroke: filled ? 'none' : 'currentColor',
      fill: filled ? 'currentColor' : 'none',
      strokeWidth: filled ? '0' : '1.25',
    }}
  />
)

// Message Actions Dropdown Menu
const MessageActionsMenu = ({
  isVisible,
  onReply,
  onForward,
  onPin,
  onStar,
  onEdit,
  onDelete,
  isMyMessage,
  message,
}) => {
  if (!isVisible) return null

  const isStarred = message?.pinned || false

  const menuItems = [
    { icon: <ReplyIcon className="w-5 h-5 text-app-grey-65" />, label: 'Reply', action: onReply },
    {
      icon: <ForwardIcon className="w-5 h-5 text-app-grey-65" />,
      label: 'Forward',
      action: onForward,
    },
    {
      icon: <StarIcon filled={isStarred} />,
      label: isStarred ? 'Unstar message' : 'Star message',
      action: onStar,
    },
  ]

  // Add edit and delete for own messages
  if (isMyMessage) {
    menuItems.push(
      { icon: <EditIcon className="w-5 h-5 text-app-grey-70" />, label: 'Edit', action: onEdit },
      {
        icon: <DeleteIcon className="w-5 h-5 text-app-grey-65" />,
        label: 'Delete',
        action: onDelete,
      }
    )
  }

  return (
    <div className="absolute right-0 top-8 bg-app-white-100 rounded-lg shadow-lg border border-app-grey-25 py-2 min-w-[160px] z-50">
      {menuItems.map((item, index) => (
        <button
          key={index}
          onClick={item.action}
          className="w-full px-4 py-2 text-left text-sm text-app-grey-70 hover:bg-app-grey-5 flex items-center gap-3"
        >
          <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            {item.icon}
          </span>
          <span className="text-app-grey-100">{item.label}</span>
        </button>
      ))}
    </div>
  )
}

// Function to highlight search terms
const highlightSearchText = (text: string, query: string) => {
  if (!query || !query.trim() || !text) return text

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedQuery})`, 'gi')
  const parts = text.split(regex)

  return parts.map((part, index) => {
    if (regex.test(part)) {
      return (
        <span key={index} className="bg-app-orange-20 text-app-orange-100 rounded font-medium">
          {part}
        </span>
      )
    }
    return part
  })
}

// (removed) detect-html helper is no longer needed because we prefer provided HTML

// Custom Message Text component that formats mentions and rich text
const CustomMessageText = ({ message, isMyMessage, searchQuery }) => {
  const hasText = message.text && message.text.trim().length > 0
  const hasAttachments = message.attachments && message.attachments.length > 0
  const hasTranscript = message.custom?.transcript && message.custom.transcript.trim().length > 0

  // Check for voice messages in attachments - more comprehensive detection
  const voiceAttachments = hasAttachments
    ? message.attachments.filter((att) => {
        if (att.type !== 'file' && att.type !== 'audio') {
          return false
        }

        const allFields = JSON.stringify(att).toLowerCase()
        if (allFields.includes('voice-message')) {
          return true
        }

        if (att.mime_type?.startsWith('audio/')) {
          return true
        }

        const fileName = att.file_name || att.name || att.title || att.fallback || att.text || ''
        if (
          typeof fileName === 'string' &&
          (fileName.endsWith('.webm') || fileName.includes('voice-message'))
        ) {
          return true
        }

        const url = att.asset_url || att.file_url || att.url || ''
        if (typeof url === 'string' && url.includes('voice-message')) {
          return true
        }

        if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio')) {
          return true
        }

        return false
      })
    : []

  const nonVoiceAttachments = hasAttachments
    ? message.attachments.filter((att) => {
        if (att.type !== 'file' && att.type !== 'audio') return true

        const allFields = JSON.stringify(att).toLowerCase()
        if (allFields.includes('voice-message')) return false

        if (att.mime_type?.startsWith('audio/')) return false

        const fileName = att.file_name || att.name || att.title || att.fallback || att.text || ''
        if (
          typeof fileName === 'string' &&
          (fileName.endsWith('.webm') || fileName.includes('voice-message'))
        )
          return false

        const url = att.asset_url || att.file_url || att.url || ''
        if (typeof url === 'string' && url.includes('voice-message')) return false

        if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio')) return false

        return true
      })
    : []

  if (!hasText && !hasAttachments && !hasTranscript) {
    return <div className="px-4 py-2 text-sm"></div>
  }

  if (voiceAttachments.length > 0) {
    return (
      <div className="text-sm text-left">
        {/* Render voice messages */}
        <div className={hasText || nonVoiceAttachments.length > 0 ? 'mb-2' : ''}>
          {voiceAttachments.map((attachment, index) => {
            const audioUrl = attachment.asset_url || attachment.file_url || attachment.url
            return (
              <VoiceMessage
                key={`voice-${index}`}
                audioUrl={audioUrl}
                duration={message.custom?.voiceDurationSeconds}
                transcript={
                  hasTranscript ? message.custom.transcript : hasText ? message.text : undefined
                }
                isMyMessage={isMyMessage}
                className="mb-2 last:mb-0"
              />
            )
          })}
        </div>

        {/* Render non-voice attachments */}
        {nonVoiceAttachments.length > 0 && (
          <div className="mt-2">
            <Attachment attachments={nonVoiceAttachments} />
          </div>
        )}
      </div>
    )
  }

  // For regular messages, render with padding
  return (
    <div className="px-2 py-2 text-sm text-left">
      {/* Render text content */}
      {(hasText || message.custom?.richTextHtml || message.html) && (
        <div className="text-left">
          {message.custom?.richTextHtml ? (
            <div
              className="prose prose-sm max-w-none [&_a]:text-app-primary-60 [&_a:hover]:text-app-primary-70 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(message.custom.richTextHtml, {
                  ADD_TAGS: ['mention'],
                  ADD_ATTR: ['id']
                })
              }}
            />
          ) : message.html ? (
            <div
              className="prose prose-sm max-w-none [&_a]:text-app-primary-60 [&_a:hover]:text-app-primary-70 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_mention]:bg-app-primary-10 [&_mention]:rounded [&_mention]:px-1 [&_mention]:py-0.5 [&_mention]:text-app-primary-70 [&_mention]:font-medium"
              dangerouslySetInnerHTML={{ 
                __html: DOMPurify.sanitize(message.html, {
                  ADD_TAGS: ['mention'],
                  ADD_ATTR: ['id']
                })
              }}
            />
          ) : (
            searchQuery ? highlightSearchText(message.text, searchQuery) : renderText(message.text)
          )}
        </div>
      )}

      {/* Render transcript if available */}
      {hasTranscript && (
        <div className="text-left text-app-grey-70 italic">
          <span className="text-xs text-app-grey-60 mr-1">Transcript:</span>
          {message.custom.transcript}
        </div>
      )}

      {/* Render non-voice attachments */}
      {nonVoiceAttachments.length > 0 && (
        <div className={hasText || hasTranscript ? 'mt-2' : ''}>
          <Attachment attachments={nonVoiceAttachments} />
        </div>
      )}
    </div>
  )
}

type MessageSimpleWithContextProps<
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
> = MessageContextValue<StreamChatGenerics>

const MessageSimpleWithContext = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  props: MessageSimpleWithContextProps<StreamChatGenerics> & { searchQuery?: string }
) => {
  const {
    editing,
    isMyMessage,
    message,
    clearEditingState,
    setEditingState,
    handleDelete,
    handleOpenThread,
    threadList,
    searchQuery,
  } = props
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showForwardModal, setShowForwardModal] = useState(false)
  const [showReactionSelector, setShowReactionSelector] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(false)
  const messageRef = useRef<HTMLDivElement>(null)

  // Popup state
  const [showMoreActions, setShowMoreActions] = useState(false)
  const moreBtnRef = useRef<HTMLButtonElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const lockedParentsRef = useRef<LockedEl[]>([])

  const { MessageDeleted = DefaultMessageDeleted } =
    useComponentContext<StreamChatGenerics>('MessageSimple')
  const { channel, client } = useChatContext<StreamChatGenerics>()
  const { userName } = useUserInfo()

  // State to store reply users
  const [replyUsers, setReplyUsers] = React.useState<any[]>([])
  const [loadingReplyUsers, setLoadingReplyUsers] = React.useState(false)
  // Fetch reply users when component mounts or reply count changes
  React.useEffect(() => {
    const fetchReplyUsers = async () => {
      if (message.reply_count && message.reply_count > 0 && channel) {
        setLoadingReplyUsers(true)
        try {
          const replies = await channel.getReplies(message.id, { limit: 3 })
          const uniqueUsers: any[] = []
          const userIds = new Set()

          for (const reply of replies.messages) {
            const user = reply.user
            if (user && !userIds.has(user.id)) {
              userIds.add(user.id)
              uniqueUsers.push(user)
              if (uniqueUsers.length === 2) break // Only need first 2 users
            }
          }

          setReplyUsers(uniqueUsers)
        } catch (error) {
          console.error('Error fetching reply users:', error)
          setReplyUsers([])
        } finally {
          setLoadingReplyUsers(false)
        }
      } else {
        setReplyUsers([])
      }
    }

    fetchReplyUsers()
  }, [message.id, message.reply_count, channel])

  // Handle edit by using setEditingState from props
  const handleEditClick = (event: React.BaseSyntheticEvent) => {
    if (setEditingState) setEditingState(event)
  }

  // Handle delete by showing the modal
  const handleDeleteClick = () => setShowDeleteModal(true)

  const handleCopy = () => {
    if (message?.text) {
      navigator.clipboard
        .writeText(message.text)

        .catch((err) => {
          console.error('Failed to copy: ', err)
        })
    }
  }
  // Close on outside click + Esc (existing behavior preserved)
  useEffect(() => {
    if (!showMoreActions) return

    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node
      if (!menuRef.current?.contains(t) && !moreBtnRef.current?.contains(t)) {
        setShowMoreActions(false)
      }
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowMoreActions(false)
    }

    document.addEventListener('mousedown', onDocClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDocClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [showMoreActions])

  // Lock BOTH page and chat scrolling while open; reposition on resize/scroll
  useEffect(() => {
    if (!showMoreActions) {
      // unlock on close
      lockedParentsRef.current.forEach(unlockElementScroll)
      lockedParentsRef.current = []
      unlockBodyScroll()
      return
    }

    // lock page scroll
    lockBodyScroll()
    // lock all scrollable parents of the trigger (e.g., chat scroller)
    const parents = getScrollParents(moreBtnRef.current)
    lockedParentsRef.current = parents.map(lockElementScroll)

    return () => {}
  }, [showMoreActions])

  // Confirm delete
  const handleConfirmDelete = async () => {
    if (handleDelete) {
      try {
        const syntheticEvent = {
          preventDefault: () => {},
          stopPropagation: () => {},
        } as React.BaseSyntheticEvent
        await handleDelete(syntheticEvent)
        setShowDeleteModal(false)
      } catch (error) {
        console.error('Failed to delete message:', error)
        setShowDeleteModal(false)
      }
    }
  }

  // Cancel delete
  const handleCancelDelete = () => setShowDeleteModal(false)

  // Reactions
  const handleSetShowEmoji = (val: any) => {
    setShowReactionSelector(val)
    return {}
  }

  // Handle thread reply using Stream Chat's built-in functionality
  const handleReply = () => {
    if (handleOpenThread && typeof handleOpenThread === 'function') {
      // Create a synthetic event for handleOpenThread
      const syntheticEvent = {
        preventDefault: () => {},
        stopPropagation: () => {},
      } as React.MouseEvent

      handleOpenThread(syntheticEvent)
    }
    setShowActionsMenu(false)
  }

  const handleForward = () => {
    setShowForwardModal(true)
    setShowActionsMenu(false)
  }

  const handleForwardConfirm = async (selectedUsers: string[], selectedChannels: any[]) => {
    try {
      console.log('Forwarding message to users:', selectedUsers, 'and channels:', selectedChannels)

      // Forward to existing channels
      for (const selectedChannel of selectedChannels) {
        const forwardMessage: any = {
          text: message.text || '',
        }
        if (message.attachments && message.attachments.length > 0) {
          forwardMessage.attachments = message.attachments
        }
        await selectedChannel.sendMessage(forwardMessage)
      }

      // Forward to selected users by creating/finding DM channels
      for (const userId of selectedUsers) {
        try {
          // Create or get existing DM channel
          const channelId = [client?.userID, userId].sort().join('-')
          const dmChannel = client?.channel('messaging', channelId, {
            members: [userId, client?.userID].filter(Boolean) as string[],
            team: 'default', // You might need to adjust this based on your setup
          })

          await dmChannel?.create()
          const forwardMessage: any = {
            text: message.text || '',
          }
          if (message.attachments && message.attachments.length > 0) {
            forwardMessage.attachments = message.attachments
          }
          await dmChannel?.sendMessage(forwardMessage)
        } catch (error) {
          console.error(`Error forwarding to user ${userId}:`, error)
        }
      }

      console.log('Message forwarded successfully')
    } catch (error) {
      console.error('Error forwarding message:', error)
    }
  }

  const handleForwardCancel = () => {
    setShowForwardModal(false)
  }

  const handlePin = () => {
    // TODO: Implement pin functionality
    console.log('Pin message:', message.id)
    setShowActionsMenu(false)
  }

  const handleStar = async () => {
    try {
      if (!client) {
        console.error('Chat client not available')
        return
      }

      if (message.pinned) {
        // If message is already pinned (starred), unpin it
        await client.unpinMessage(message)
        console.log('Message unstarred (unpinned):', message.id)
      } else {
        // If message is not pinned, pin it (star it)
        await client.pinMessage(message)
        console.log('Message starred (pinned):', message.id)
      }

      // Dispatch custom event to notify other components
      window.dispatchEvent(
        new CustomEvent('chat-event', {
          detail: { type: 'message-starred', messageId: message.id, pinned: !message.pinned },
        })
      )
    } catch (error) {
      console.error('Error toggling star on message:', error)
      // You can add a toast notification here if you have one
    }
    setShowActionsMenu(false)
  }

  // Mouse event handlers for hover state
  const handleMouseLeave = () => {
    setShowActionsMenu(false)
  }

  // Handle three dots click
  const handleThreeDotsClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setShowActionsMenu(!showActionsMenu)
  }

  const messageClasses = isMyMessage()
    ? 'str-chat__message str-chat__message--me str-chat__message-simple str-chat__message-simple--me'
    : 'str-chat__message str-chat__message-simple'

  if (message.customType === CUSTOM_MESSAGE_TYPE.date) return null

  if (message.deleted_at || message.type === 'deleted') {
    return <MessageDeleted message={message} />
  }

  let messageDetails = message
  const isFrozen = get(channel, 'data.frozen') && message.type === 'error'
  if (isFrozen) {
    messageDetails = {
      ...message,
      text: 'Sorry, this channel has been frozen by the admin',
      type: '',
    }
  }
  return (
    <>
      <div className={`${messageClasses}`} ref={messageRef} onMouseLeave={handleMouseLeave}>
        <EditMessageForm show={editing} message={message} clearEditingState={clearEditingState} />
        {!editing && message && (
          <React.Fragment>
            <div className="mt-2 w-full relative">
              <div className={`flex gap-3 ${isMyMessage() ? 'justify-end' : 'justify-start'}`}>
                {!isMyMessage() && (
                  <AppAvatar
                    size="md"
                    type="mixed"
                    shape="Rounded"
                    text={getInitials({
                      firstName: messageDetails.user?.firstName as string,
                      lastName: messageDetails.user?.lastName as string,
                    })}
                  />
                )}
                <div
                  className={`flex flex-col max-w-[75%] overflow-visible ${
                    isMyMessage() ? 'items-end' : 'items-start'
                  }`}
                >
                  <div
                    className={`text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1 ${
                      isMyMessage() ? 'mr-1 justify-end' : 'ml-1 justify-start'
                    }`}
                  >
                    <span>
                      {isMyMessage()
                        ? 'You'
                        : `${messageDetails.user?.firstName} ${messageDetails.user?.lastName}`}
                    </span>
                    {messageDetails.pinned && (
                      <div className="text-yellow-500" title="Starred message">
                        <StarIcon filled={true} />
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    {/* Check if message has voice attachments to render differently */}
                    {messageDetails.attachments?.some((att) => {
                      if (att.type !== 'file' && att.type !== 'audio') return false

                      // AGGRESSIVE DETECTION: Check for voice-message pattern in any field
                      const allFields = JSON.stringify(att).toLowerCase()
                      if (allFields.includes('voice-message')) return true

                      if (att.mime_type?.startsWith('audio/')) return true
                      const fileName =
                        att.file_name || att.name || att.title || att.fallback || att.text || ''
                      if (
                        typeof fileName === 'string' &&
                        (fileName.endsWith('.webm') || fileName.includes('voice-message'))
                      )
                        return true
                      const url = att.asset_url || att.file_url || att.url || ''
                      if (typeof url === 'string' && url.includes('voice-message')) return true
                      if (att.mime_type?.includes('webm') || att.mime_type?.includes('audio'))
                        return true
                      return false
                    }) ? (
                      // Render voice message without background bubble
                      <CustomMessageText
                        message={messageDetails}
                        isMyMessage={isMyMessage()}
                        searchQuery={searchQuery}
                      />
                    ) : (
                      // Render regular message with bubble
                      <div
                        className={`rounded-2xl ${
                          isMyMessage() ? 'bg-app-primary-10 text-white' : 'bg-app-grey-5'
                        }`}
                      >
                        <CustomMessageText
                          message={messageDetails}
                          isMyMessage={isMyMessage()}
                          searchQuery={searchQuery}
                        />
                      </div>
                    )}

                    {/* Three dots menu button - appears on hover */}
                  </div>

                  {/* Reaction selector */}
                  {showReactionSelector && (
                    <Portal>
                      <div
                        className="fixed z-[2000] left-1/2 top-1/2"
                        style={{ transform: 'translate(-50%, 0)' }}
                      >
                        <ReactionSelector setShowEmoji={handleSetShowEmoji} />
                      </div>
                    </Portal>
                  )}

                  {/* Thread replies button - only show when there are replies */}
                  {messageDetails.reply_count && messageDetails.reply_count > 0 && !threadList ? (
                    <div className="mt-2 px-2">
                      <button
                        onClick={handleReply}
                        className="flex items-center gap-2 px-3 py-2 text-xs cursor-pointer hover:bg-app-grey-5 rounded-lg transition-colors"
                      >
                        {/* User avatars - show reply users */}
                        <div className="flex -space-x-1">
                          {loadingReplyUsers ? (
                            // Loading state
                            <div className="w-6 h-6 bg-app-grey-30 rounded-full animate-pulse"></div>
                          ) : (
                            <>
                              {/* First reply user avatar */}
                              {replyUsers[0] && (
                                <div className="relative border-2 border-white rounded-full">
                                  <AppAvatar
                                    size="xs"
                                    type="mixed"
                                    shape="Rounded"
                                    text={getInitials({
                                      firstName:
                                        replyUsers[0].firstName ||
                                        replyUsers[0].name?.split(' ')[0] ||
                                        '',
                                      lastName:
                                        replyUsers[0].lastName ||
                                        replyUsers[0].name?.split(' ')[1] ||
                                        '',
                                    })}
                                  />
                                </div>
                              )}
                              {/* Second reply user avatar or plus badge */}
                              {messageDetails.reply_count > 1 && (
                                <div className="relative" style={{ zIndex: 9 }}>
                                  {replyUsers[1] && messageDetails.reply_count === 2 ? (
                                    <div className="relative border-2 border-white rounded-full">
                                      <AppAvatar
                                        size="xs"
                                        type="mixed"
                                        shape="Rounded"
                                        text={getInitials({
                                          firstName:
                                            replyUsers[1].firstName ||
                                            replyUsers[1].name?.split(' ')[0] ||
                                            '',
                                          lastName:
                                            replyUsers[1].lastName ||
                                            replyUsers[1].name?.split(' ')[1] ||
                                            '',
                                        })}
                                      />
                                    </div>
                                  ) : (
                                    <div className="w-6 h-6 bg-app-grey-40 border-2 border-white rounded-full flex items-center justify-center text-[10px] font-semibold text-app-grey-80">
                                      +{messageDetails.reply_count - 1}
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        <span className="font-medium text-app-grey-70 hover:text-app-grey-90">
                          {messageDetails.reply_count === 1
                            ? '1 reply'
                            : `${messageDetails.reply_count} replies`}
                        </span>
                      </button>
                    </div>
                  ) : null}

                  <div className="text-xs text-silver-darker mt-1 px-1 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isFrozen ? (
                        <span className="relative text-primary-error inline-block items-center mr-2 font-bold">
                          <ExclamationMarkIcon className="absolute fill-primary-error hover:fill-primary-error h-[14px] w-[14px] mr-1 inline-block left-[-18px] top-[2px]" />
                          Unsent
                        </span>
                      ) : null}

                      <div className="flex">
                        {!isFrozen && (
                          <div>
                            {isMyMessage() && (
                              <button
                                className="mx-1"
                                title="Edit message"
                                onClick={(e) => {
                                  e.preventDefault()
                                  handleEditClick(e)
                                }}
                              >
                                <PenIcon />
                              </button>
                            )}
                            <button
                              className="mx-1"
                              onClick={handleCopy}
                              role="menuitem"
                              title="Copy"
                            >
                              <CopyIcon />
                            </button>
                            {isMyMessage() && (
                              <button
                                className="mx-1"
                                onClick={handleDeleteClick}
                                title="Delete message"
                              >
                                <DeleteIcon className="w-5 h-5 text-app-grey-65" />
                              </button>
                            )}
                            <button
                              className="mx-1"
                              onClick={handleThreeDotsClick}
                              title="More options"
                            >
                              <ThreeDotsIcon />
                            </button>

                            {/* Actions dropdown menu */}
                            <MessageActionsMenu
                              isVisible={showActionsMenu}
                              onReply={handleReply}
                              onForward={handleForward}
                              onPin={handlePin}
                              onStar={handleStar}
                              onEdit={handleEditClick}
                              onDelete={handleDeleteClick}
                              isMyMessage={isMyMessage()}
                              message={messageDetails}
                            />
                          </div>
                        )}
                      </div>
                      {/* Message options for reactions */}
                      <MessageOptions
                        setShowEmoji={handleSetShowEmoji}
                        showEmoji={showReactionSelector}
                      />
                      {/* Display existing reactions inline */}
                      <SimpleReactionsList />
                    </div>
                  </div>
                </div>

                {isMyMessage() && (
                  <AppAvatar
                    size="md"
                    type="mixed"
                    shape="Rounded"
                    text={getInitialsFromName(userName)}
                  />
                )}
              </div>
            </div>
          </React.Fragment>
        )}
      </div>

      {/* Delete Message Modal */}
      <DeleteMessageModal
        show={showDeleteModal}
        message={message}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />

      {/* Forward Message Modal */}
      <ForwardMessageModal
        show={showForwardModal}
        message={message}
        onCancel={handleForwardCancel}
        onConfirm={handleForwardConfirm}
      />
    </>
  )
}

const MemoizedMessageSimple = React.memo(
  MessageSimpleWithContext,
  areMessageUIPropsEqual
) as typeof MessageSimpleWithContext

export const MessageSimple = <
  StreamChatGenerics extends DefaultStreamChatGenerics = DefaultStreamChatGenerics
>(
  props: MessageUIComponentProps<StreamChatGenerics>
) => {
  const messageContext = useMessageContext<StreamChatGenerics>('MessageSimple')
  return <MemoizedMessageSimple {...messageContext} {...props} />
}
